import { useRouter } from "@core/Api/useRouter";
import { type UseApiProps, useDeferApi } from "@core-ui/hooks/useApi";
import { usePlatform } from "@core-ui/hooks/usePlatform";
import type GitStorageData from "@ext/git/core/model/GitStorageData";
import { useCallback } from "react";
import type StorageData from "../../../../storage/models/StorageData";
import getClonePathname from "./getClonePathname";

export type StartCloneResponse = { alreadyExist: boolean };

export type UseCloneRepoProps = {
	storageData?: StorageData;
	skipCheck?: boolean;
	branch?: string;
	isBare?: boolean;
	redirectOnClone?: string;
	deleteIfExists?: boolean;
	skipLfsPull?: boolean;
} & Pick<UseApiProps, "onStart" | "onError">;

export type StartCloneProps = {
	data: StorageData;
};

export const useCloneRepo = ({ onError, ...props }: UseCloneRepoProps) => {
	const router = useRouter();

	const { isNext } = usePlatform();
	const { redirectOnClone } = props;

	const redirect = useCallback(
		(data: GitStorageData) => {
			if (isNext) return;
			if (!redirectOnClone && !data) return;

			const path = redirectOnClone || getClonePathname(data);

			if (path) void router.pushPath(path);
		},
		[router, isNext, redirectOnClone],
	);

	const { call } = useDeferApi<StartCloneResponse>({
		onError,
	});

	const startClone = async (defer?: UseCloneRepoProps) => {
		const data = defer ? { ...props, ...defer } : props;

		if (!data.storageData) return;

		return await call({
			url: (api) =>
				api.startClone(
					data.storageData.name,
					data.isBare,
					data.skipCheck,
					data.branch,
					data.deleteIfExists,
					data.skipLfsPull,
				),
			opts: {
				// Not in the query: the web fetch service decodes query values, turning a nested group's `%2F` into `/`.
				body: { data: data.storageData, redirectOnClone: data.redirectOnClone },
				parse: "json",
			},
			onDone: ({ alreadyExist }) =>
				alreadyExist ? redirect(data.storageData as unknown as GitStorageData) : data.onStart?.(),
		});
	};

	return { startClone };
};
